// PostImage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./media.css";

/**
 * <PostImage />
 * - Reserves vertical space using an aspect-ratio shim (no CLS/flicker).
 * - Shows a shimmer skeleton until the real image has decoded.
 * - Renders only when `shouldLoad` || `priority` is true (avoids early downloads).
 *
 * Props:
 *   src, alt, className, lowSrc, priority, shouldLoad, style, width, height, fallbackAspect
 *
 * Backwards-compatible: if you don't pass shouldLoad it will load immediately (preserve old behavior).
 */
const TRANSPARENT_PX =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

export default function PostImage({
  src,
  alt = "",
  className = "",
  lowSrc,              // optional tiny preview (data URL or small jpg/webp)
  priority = false,    // if true => load eagerly with fetchpriority=high
  shouldLoad = true,   // set to false to defer actual src until near viewport
  onClick,
  onKeyDown,
  style,
  width,
  height,
  fallbackAspect = 4 / 5,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // displaySrc starts as either real src (if immediate load), lowSrc, or a tiny transparent image
  const [displaySrc, setDisplaySrc] = useState(() => {
    if (priority || shouldLoad) return src || lowSrc || TRANSPARENT_PX;
    return lowSrc || TRANSPARENT_PX;
  });

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => {
    setErrored(true);
    setLoaded(false);
  }, []);

  // When shouldLoad or priority becomes true, preload & decode the actual src, then swap in.
  useEffect(() => {
    if (!src) return;
    if (displaySrc === src) return; // already showing real src

    if (priority || shouldLoad) {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
      img.onload = () => {
        setDisplaySrc(src);
        setLoaded(true);
      };
      img.onerror = () => {
        setErrored(true);
      };
    }
    // we intentionally don't include displaySrc in deps to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priority, shouldLoad, src]);

  // Use exact ratio if provided; otherwise use a safe fallback
  const ratio = useMemo(() => {
    if (width && height && height !== 0) return width / height;
    return fallbackAspect;
  }, [width, height, fallbackAspect]);

  const paddingTop = useMemo(() => `${(1 / ratio) * 100}%`, [ratio]);

  // Decide loading attribute: if priority -> eager, else preserve passed rest.loading or lazy
  const loadingAttr = priority ? "eager" : (rest.loading || "lazy");
  const fetchPriority = priority ? "high" : "auto";

  return (
    <div className="bf-mediaWrap" style={style}>
      {/* Ratio shim: locks the final height immediately */}
      <div className="bf-shim" style={{ paddingTop }} />

      {/* Skeleton overlay until the image is decoded */}
      {!loaded && !errored && (
        <div
          className="bf-skeleton"
          style={{ backgroundImage: lowSrc && displaySrc !== lowSrc ? `url("${lowSrc}")` : "none" }}
        />
      )}

      {/* The actual image (absolute, fills wrapper) */}
      <img
        src={displaySrc}
        alt={alt}
        width={width}
        height={height}
        className={`${className} bf-img ${loaded ? "bf-loaded" : ""} ${errored ? "bf-err" : ""}`}
        loading={loadingAttr}
        decoding="async"
        fetchpriority={fetchPriority}
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...rest}
      />
    </div>
  );
}
